// utils/analytics.ts
// Privacy-friendly, vendor-free analytics. Events go nowhere unless
// VITE_ANALYTICS_URL is configured (then via sendBeacon with keepalive
// fallback); Do-Not-Track is always honoured. Until a backend endpoint or a
// vendor SDK exists, events are debug-logged so call sites stay stable and
// future wiring is a config change, not a code change.
export interface AnalyticsPayload {
  event: string;
  props: Record<string, unknown>;
  session: string;
  url?: string;
  at: string;
}

let sessionId: string | null = null;

function getSessionId(): string {
  if (!sessionId) {
    try {
      const bytes = new Uint32Array(4);
      (crypto as any)?.getRandomValues?.(bytes);
      sessionId = Array.from(bytes, (b) => b.toString(16)).join('');
    } catch {
      sessionId = String(Date.now());
    }
    if (!sessionId) sessionId = String(Date.now());
  }
  return sessionId;
}

export function isTrackingAllowed(): boolean {
  try {
    return (navigator as any)?.doNotTrack !== '1';
  } catch {
    return true;
  }
}

export function trackEvent(event: string, props: Record<string, unknown> = {}): void {
  try {
    if (!event || !isTrackingAllowed()) return;
    const payload: AnalyticsPayload = {
      event,
      props,
      session: getSessionId(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      at: new Date().toISOString(),
    };
    const endpoint = (import.meta as any)?.env?.VITE_ANALYTICS_URL as string | undefined;
    if (!endpoint) {
      console.debug('[analytics]', payload);
      return;
    }
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(String(endpoint), body);
    } else if (typeof fetch === 'function') {
      fetch(String(endpoint), { method: 'POST', body, keepalive: true }).catch((err) => { void err; });
    }
  } catch {
    /* analytics must never break the app */
  }
}

export function trackPageView(path: string): void {
  trackEvent('page_view', { path });
}

/** Test-only reset for the session cache. */
export function __resetAnalyticsForTests(): void {
  sessionId = null;
}
